import React, { useEffect, useState } from 'react';
import ButtonCustom from '../../common/__deprecated__/button-custom';
import Card from '../../common/card';
import { ReactComponent as CheckGreenIcon } from '../../../assets/icons/check-green.svg';
import { ReactComponent as ClockYellowIcon } from '../../../assets/icons/clock-yellow.svg';
import { ReactComponent as TransactionIcon } from '../../../assets/icons/transactions.svg';
import './styles.scss';
import { useDashboard } from '../../../store/dashboard';
import Table from '../../common/table';
import TablePagination from '../../common/table-pagination';
import StatsCircularBar from './stats-circular-bar';
import { Box, Divider, useMediaQuery } from '@mui/material';
import Parser from 'html-react-parser';
import l from '../../../lang';
import { EmptyStats } from './empty-stats';
import ChipStatus from '../../common/chip-status';
import DialogConfirm from '../../common/confirm/dialog-confirm';
import CardConfirm from '../../common/confirm/card-confirm';
import TopDashboardSection from '../../common/top-dashboard-section';
import SplitsHeaderURL from '../../../assets/images/splits-header.svg?url';
import InputSearch from '../../common/input-search';
import { useSplitPayments } from '../../../store/split-payments';
import { useHistory } from 'react-router-dom';
import { Routes } from '../../../config/routes';
import ButtonMoreMenu from '../../common/button-more-menu';
import { SplitPayment } from '../../../store/split-payments/interfaces';
import SplitContractDrawer from './split-contract-drawer';

const SplitPayments = () => {
  const { merchantMe } = useDashboard();
  const isTabletMobile = useMediaQuery('(max-width:768px)');
  const [showRequestCancelDialog, setShowRequestCancelDialog] = useState(false);
  const [collaborationIdToCancel, setCollaborationIdToCancel] = useState<number | null>(null);
  const { inviteToCollaborate, getSplitPayments, allSplitPayments, loading, cancelCollaboration, selectCollaboration } =
    useSplitPayments();
  const history = useHistory();
  const [isDetailsShown, setIsDetailsShown] = useState(false);
  const [detailContract, setDetailContract] = useState<SplitPayment | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const PAGE_SIZE = 10;

  useEffect(() => {
    getSplitPayments(0);
  }, []);

  const itIsAnInvitation = (splitPayment: SplitPayment) => {
    const isCollaborator = splitPayment.collaboratorId === merchantMe?.id;
    const isPending = splitPayment.status === 'PENDING';
    const isHaventAccepted =
      (isCollaborator && !splitPayment.collaboratorAcceptedAt) || (!isCollaborator && !splitPayment.sellerAcceptedAt);

    return isPending && isHaventAccepted;
  };

  const handleInvitation = () => {
    inviteToCollaborate();
    history.push(Routes.SPLIT_PAYMENTS_STEP_USER_TYPE);
  };

  const handleAcceptInvitation = (splitPayment: SplitPayment) => {
    history.push(Routes.SPLIT_PAYMENTS_RESUME.replace(':collaboration_id', String(splitPayment.id)));
  };

  const handleRequestCancel = (splitPayments: SplitPayment) => {
    setShowRequestCancelDialog(true);
    setCollaborationIdToCancel(splitPayments.id ?? 0);
  };

  const handleCancelCollaboration = (flag: boolean) => {
    if (flag) {
      cancelCollaboration(collaborationIdToCancel ?? 0);
      setCollaborationIdToCancel(null);
      setShowRequestCancelDialog(false);
      getSplitPayments(0);
    }
  };

  const handleCollaborationInfo = (splitPayment: SplitPayment) => {
    setDetailContract(splitPayment);
    setIsDetailsShown(true);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setCurrentPage(newPage);
    getSplitPayments(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // PAGE_SIZE is fixed at 10, so we don't allow changing it
    // But we implement this handler for completeness
  };

  return (
    <div className="split-payments">
      <TopDashboardSection title={l('split_payments.title')} subtitle={l('split_payments.subtitle')} />

      <Card>
        <div style={{ textAlign: 'initial' }}>
          <div className="split-payments__header_card">
            <Card>
              <div className="splits-card">
                <div className="splits-card__win">
                  <div className="splits-card__win__desc">
                    <p className="splits-card__win__desc__bold">{l('split_payments.desc.divide_payments')}</p>
                    <p>{l('split_payments.desc.optimize_operation')}</p>
                    <ButtonCustom onClick={handleInvitation} text={l('split_payments.invite_collaborate')} />
                  </div>
                </div>
                <div className="splits-card__img">
                  <img src={SplitsHeaderURL} alt="" />
                </div>
              </div>
            </Card>
          </div>

          {showRequestCancelDialog && (
            <DialogConfirm onClose={() => setShowRequestCancelDialog(false)}>
              <CardConfirm
                onClose={flag => handleCancelCollaboration(flag)}
                headline={l('split_payments.cancel.headline')}
                description={l('split_payments.cancel.description')}
                confirmChildren={l('common.confirm')}
                level={'info'}
              />
            </DialogConfirm>
          )}

          <div className="split-payments__search">
            <InputSearch
              value={searchValue}
              searchOptions={[
                { value: 'email', label: 'Email' },
                { value: 'merchantId', label: 'Merchant ID' },
                { value: 'fantasyName', label: l('split_payments.search.fantasy_name') },
                { value: 'code', label: l('split_payments.search.code') },
              ]}
              placeholder={l('split_payments.search.placeholder')}
              isLoading={loading}
              helpText={''}
              onSearch={(type, value) => {
                setSearchValue(value);
                setCurrentPage(0);
                getSplitPayments(0, { [type]: value });
              }}
            />
          </div>

          <h3>Split Payments</h3>
          <Table
            emptyBody={<></>}
            headers={[
              <span>{l('split_payments.table.type')}</span>,
              <span>{l('split_payments.table.id')}</span>,
              <span>{l('split_payments.table.integrator_code')}</span>,
              <span>{l('split_payments.table.name')}</span>,
              <span>{l('split_payments.table.transactions')}</span>,
              <span>{l('split_payments.table.amount')}</span>,
              <span>{l('split_payments.table.collaborator_balance')}</span>,
              <span>{l('split_payments.table.seller_balance')}</span>,
              <span>{l('split_payments.table.fixed_collaborator_commission')}</span>,
              <span>{l('split_payments.table.status')}</span>,
            ]}
            body={
              allSplitPayments?.data
                ? allSplitPayments?.data.map((splitPayment, index) => [
                    <span>
                      {splitPayment.collaboratorId === merchantMe?.id
                        ? l('split_payments.creation.user_type.collaborator')
                        : l('split_payments.creation.user_type.seller')}
                    </span>,
                    <span>{String(index + 1).padStart(2, '0')}</span>,
                    <span>{splitPayment.code}</span>,
                    <span>{splitPayment.name}</span>,
                    <span>{splitPayment.countCompletedTransactions}</span>,
                    <span>{`${splitPayment.sellerFunctionalCurrency} ${Number(splitPayment.sumBalanceCollectedAmount).toFixed(2)}`}</span>,
                    <span>{`${splitPayment.collaboratorFunctionalCurrency} ${Number(splitPayment.sumCollaboratorBalanceAmount).toFixed(2)}`}</span>,
                    <span>{`${splitPayment.sellerFunctionalCurrency} ${Number(splitPayment.sumBalanceAmountWithoutCommission).toFixed(2)}`}</span>,
                    <span>{splitPayment.collaboratorCommission.toFixed(2) + '%'}</span>,
                    <span>
                      <ChipStatus status={splitPayment.status} text={l(`split_payments.status.${splitPayment.status}`)} />
                    </span>,
                    <ButtonMoreMenu
                      items={[
                        {
                          content: l('split_payments.table.action.information'),
                          onClick: () => handleCollaborationInfo(splitPayment),
                        },
                        {
                          content: l('split_payments.table.action.cancel'),
                          onClick: () => handleRequestCancel(splitPayment),
                          hidden: itIsAnInvitation(splitPayment) || splitPayment.status === 'CANCELLED',
                        },
                        {
                          content: l('split_payments.table.action.manage'),
                          onClick: () => handleAcceptInvitation(splitPayment),
                          hidden: !itIsAnInvitation(splitPayment),
                        },
                      ]}
                    />,
                  ])
                : [
                    [
                      <span>---</span>,
                      <span>---</span>,
                      <span>---</span>,
                      <span>---</span>,
                      <span>---</span>,
                      <span>---</span>,
                      <span>---</span>,
                      <span>---</span>,
                      <span>---</span>,
                    ],
                  ]
            }
          />
          <TablePagination
            rowsPerPageOptions={[10]}
            rowsLength={allSplitPayments?.totalElements || 0}
            rowsPerPage={PAGE_SIZE}
            page={currentPage}
            handleChangePage={handlePageChange}
            handleChangeRowsPerPage={handleRowsPerPageChange}
          />
          {isDetailsShown && <SplitContractDrawer data={detailContract} onClose={() => setIsDetailsShown(false)} />}

          {/* <h3>Resumen</h3> */}
          {/* <Card> */}
          {/* {affiliationsInfo.data?.enabled && (
              <Box className="affiliations-stats">
                <StatsCircularBar
                  descTitle={l('affiliations.affiliates.stats.activeAffiliates')}
                  descValue={`${Number(affiliationsInfo.data.stats.totalActiveAffiliates)}`}
                  percentage={Number(
                    (
                      (affiliationsInfo.data.stats.totalActiveAffiliates /
                        (affiliationsInfo.data.stats.totalAffiliates != 0
                          ? affiliationsInfo.data.stats.totalAffiliates
                          : 1)) *
                      100
                    ).toFixed(2)
                  )}
                ></StatsCircularBar>
                {!isTabletMobile && <Divider orientation="vertical" flexItem sx={{ marginRight: '20px' }} />}
                <StatsCircularBar
                  descTitle={l('affiliations.affiliates.stats.accumulate')}
                  descValue={merchantMe?.functionalCurrency + ' ' + affiliationsInfo.data.stats.totalEarnedCredits}
                  percentage={43.37}
                ></StatsCircularBar>
                {!isTabletMobile && <Divider orientation="vertical" flexItem sx={{ marginRight: '20px' }} />}
                <StatsCircularBar
                  descTitle={l('affiliations.affiliates.stats.usage')}
                  descValue={merchantMe?.functionalCurrency + ' ' + affiliationsInfo.data.stats.totalAppliedCredits}
                  percentage={Number(
                    (
                      (affiliationsInfo.data.stats.totalAppliedCredits /
                        (affiliationsInfo.data.stats.totalEarnedCredits != 0
                          ? affiliationsInfo.data.stats.totalEarnedCredits
                          : 1)) *
                      100
                    ).toFixed(2)
                  )}
                ></StatsCircularBar>
              </Box>
            )} */}
          {/* {<EmptyStats />} */}
          {/* </Card> */}
        </div>
      </Card>
    </div>
  );
};

export default SplitPayments;
